import { useEffect, useMemo, useRef, useState } from "react";

import { Layers, Edit } from "lucide-react";

import { Select } from "antd";

import { Reactable } from "../../components/ui/reactable";

import { Button } from "../../components/ui/button";

import { MASTER_BLOCK, MASTER_FLOOR, BRANCH } from "../../constants/api";

import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";

import { DialogModal } from "../../components/ui/dialogModal";

import { Input, Textarea } from "../../components/ui/input";

import useApiMethods from "../../lib/useApiMethods";

import ToastMsg from "../../components/common/ToastMsg";

import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import BranchFilterDropdown, { masterFormSelectProps } from "../../components/common/BranchFilterDropdown";

import { isBranchManagementEnabled } from "../../utils/customConfigSession";
import useBranchRole from "../../hooks/useBranchRole";
import { getActiveBranchId, isOrgAdminUser, isOrgOverview } from "../../utils/branchSession";



const { Option } = Select;



const FloorMaster = () => {

    const { create, update, fetch } = useApiMethods();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const branchEnabled = isBranchManagementEnabled();
    const { isBranchManager, branchId: loginBranchId } = useBranchRole();
    const lockedBranchId = loginBranchId
        ? (Number(loginBranchId) || loginBranchId)
        : undefined;

    const [branches, setBranches] = useState([]);
    const [pageBranchFilter, setPageBranchFilter] = useState("all");

    const showPageBranchFilter =
        branchEnabled && isOrgAdminUser() && !isBranchManager && branches.length > 0;

    const branchListFilter = useMemo(() => {
        if (isBranchManager && lockedBranchId) {
            return { branch: lockedBranchId };
        }
        if (showPageBranchFilter && pageBranchFilter !== "all") {
            return { branch: pageBranchFilter };
        }
        if (isOrgAdminUser() && !isOrgOverview()) {
            const activeBranch = getActiveBranchId();
            if (activeBranch && activeBranch !== "all") {
                return { branch: activeBranch };
            }
        }
        return {};
    }, [isBranchManager, lockedBranchId, showPageBranchFilter, pageBranchFilter]);

    const branchFilterOptions = useMemo(
        () => branches.map((branch) => ({
            value: branch.name,
            label: branch.is_main_branch ? `${branch.name} (Main)` : branch.name,
        })),
        [branches]
    );

    const [data, setData] = useState([]);

    const [totalRecords, setTotalRecords] = useState(0);

    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);

    const [loading, setLoading] = useState(false);

    const [editMode, setEditMode] = useState(false);

    const [blocks, setBlocks] = useState([]);

    const [formData, setFormData] = useState({

        id: null,

        code: "",

        floor_name: "",

        block: undefined,

        notes: "",

        is_active: true,

    });



    const { add: addPermission, edit: editPermission } =

        checkUserPermissionFunc("master", "floor", "SubMenu");



    useEffect(() => {
        if (!branchEnabled || isBranchManager) return;
        fetchRef.current(BRANCH, { drop_down: "True" })
            .then((res) => {
                const raw = res?.data?.result || res?.data || [];
                setBranches(Array.isArray(raw) ? raw : []);
            })
            .catch(() => setBranches([]));
    }, [branchEnabled, isBranchManager]);

    useEffect(() => {
        fetchRef.current(MASTER_BLOCK, {
            drop_down: "True",
            is_active: "True",
            ...branchListFilter,
        })
            .then((res) => {
                const raw = res?.data?.result || res?.data || [];
                setBlocks(Array.isArray(raw) ? raw : []);
            })
            .catch(() => setBlocks([]));
    }, [serverTrigger, branchListFilter]);



    const resetForm = () => {

        setFormData({ id: null, code: "", floor_name: "", block: undefined, notes: "", is_active: true });

        setEditMode(false);

        setOpenDialog(false);

    };



    const openAddDialog = () => {

        setFormData({ id: null, code: "", floor_name: "", block: undefined, notes: "", is_active: true });

        setEditMode(false);

        setOpenDialog(true);

    };



    const columns = useMemo(() => {

        const cols = [

            { accessor: "code", Header: "Code", filterable: true },

            { accessor: "floor_name", Header: "Floor Name", sortable: true, filterable: true },

            { accessor: "block_name", Header: "Block", filterable: true, Cell: ({ value }) => value || "-" },

        ];

        if (branchEnabled) {

            cols.push({
                accessor: "branch_name",
                Header: "Branch",
                filterable: true,
                filterType: "select",
                filterOptions: branchFilterOptions,
                Cell: ({ value }) => value || "-",
            });

        }

        cols.push(

            { accessor: "notes", Header: "Description", Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description") },

            {

                accessor: "is_active",

                Header: "Status",

                filterable: true,

                Cell: ({ value }) => (

                    <span className={cn(

                        "px-2 py-1 text-xs font-medium rounded-full",

                        value ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"

                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"

                    )}>

                        {value ? "Active" : "Inactive"}

                    </span>

                ),

            }

        );

        if (editPermission) {

            cols.push({

                accessor: "action",

                Header: "Actions",

                sortable: false,

                Cell: ({ row }) => (

                    <Button variant="ghost" size="sm" className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"

                        onClick={() => {

                            setEditMode(true);

                            setFormData({

                                id: row?.id || null,

                                code: row?.code || "",

                                floor_name: row?.floor_name || "",

                                block: row?.block_id || undefined,

                                notes: row?.notes || "",

                                is_active: row?.is_active ?? true,

                            });

                            setOpenDialog(true);

                        }}>

                        <Edit className="h-4 w-4" />

                    </Button>

                ),

            });

        }

        return cols;

    }, [branchEnabled, editPermission, branchFilterOptions]);



    const handleSave = async () => {

        if (!formData.floor_name?.trim()) {

            ToastMsg("error", "Floor name is required");

            return;

        }

        if (!formData.block) {

            ToastMsg("error", "Block is required");

            return;

        }

        try {

            setLoading(true);

            const payload = { ...formData, is_draft: false };

            const response = editMode

                ? await update(MASTER_FLOOR, payload)

                : await create(MASTER_FLOOR, payload);

            if (response?.status === "success") {

                ToastMsg("success", response.message || "Floor saved");

                resetForm();

                setServerTrigger((p) => !p);

            } else {

                ToastMsg("error", response?.message || "Failed to save floor");

            }

        } finally {

            setLoading(false);

        }

    };



    return (

        <ContainerCard>

            <ModuleTitleCard

                Icon={Layers}

                title="Floor"

                subtitle="Manage floors linked to blocks"

                totalRecords={totalRecords}

                buttonEnable={!!addPermission || !!editPermission}

                setOpenDialog={openAddDialog}

                buttonName="Add Floor"

            />

            {showPageBranchFilter && (
                <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
                    <BranchFilterDropdown
                        branches={branches}
                        value={pageBranchFilter}
                        onChange={setPageBranchFilter}
                    />
                </div>
            )}

            <Reactable

                columns={columns}

                data={data}

                setData={setData}

                setTotalCount={setTotalRecords}

                url={MASTER_FLOOR}

                filter={branchListFilter}

                serverSide

                serverTrigger={serverTrigger}

                rowSize={editPermission ? "sm" : null}

                isCSVRequired

                isPDFRequired

                isDateRequired={false}

            />

            <DialogModal

                open={openDialog}

                onClose={resetForm}

                onSave={handleSave}

                title={editMode ? "Edit Floor" : "Add Floor"}

                closeLabel="Clear"

                saveLabel={editMode ? "Update" : "Save"}

                editMode={editMode}

                loading={loading}

                width="420px"

            >

                <div className="space-y-4">

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>

                            Block <span className="text-red-500">*</span>

                        </label>

                        <Select
                            {...masterFormSelectProps}
                            placeholder="Select block"
                            value={formData.block}
                            onChange={(value) => setFormData({ ...formData, block: value })}
                            disabled={loading}
                        >

                            {blocks.map((b) => (

                                <Option key={b.id} value={b.id}>

                                    {b.block_name}{b.branch_name ? ` (${b.branch_name})` : ""}

                                </Option>

                            ))}

                        </Select>

                    </div>

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>

                            Floor Name <span className="text-red-500">*</span>

                        </label>

                        <Input

                            value={formData.floor_name}

                            onChange={(e) => setFormData({ ...formData, floor_name: e.target.value })}

                            placeholder="Enter floor name"

                            disabled={loading}

                        />

                    </div>

                    <div className="space-y-2">

                        <label className={modalFieldLabelClass}>Description</label>

                        <Textarea

                            value={formData.notes}

                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}

                            placeholder="Optional notes"

                            rows={3}

                            maxLength={50}

                            showCharCount
                            disabled={loading}
                            className={modalTextareaClass}
                        />

                    </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label
                            htmlFor="is_active"
                            className={modalFieldLabelClass}
                        >
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                </div>

            </DialogModal>

        </ContainerCard>

    );

};



export default FloorMaster;


